import { HTTPError, TimeoutError } from 'ky'

/**
 * El sobre de error REAL del backend, verificado contra el servidor local:
 *
 *     {"error": {"code": "validation_error",
 *                "message": "Los datos enviados no son válidos.",
 *                "details": {"fields": [{"loc": ["body", "title"],
 *                                        "msg": "Field required",
 *                                        "type": "missing"}]}}}
 *
 * Antes esto se leía como `{detail, message, code, errors}` —la forma por
 * defecto de FastAPI— y el resultado era que TODOS los mensajes del backend se
 * perdían: la UI mostraba siempre el texto genérico del status y `fieldErrors`
 * quedaba vacío incluso en un 422. Los campos planos se siguen aceptando abajo
 * porque un 502 de nginx o un proxy no hablan este dialecto.
 */
export interface ApiErrorEnvelope {
  error?: {
    code?: string
    message?: string
    details?: {
      fields?: { loc?: (string | number)[]; msg?: string; type?: string }[]
      [key: string]: unknown
    }
  }
  /** Formas ajenas al backend (FastAPI crudo, proxies, handlers viejos). */
  detail?: string | { msg?: string; loc?: (string | number)[] }[]
  message?: string
  code?: string
  errors?: Record<string, string[]>
  request_id?: string
}

/** @deprecated Nombre viejo; se mantiene para no romper imports. */
export type ApiErrorBody = ApiErrorEnvelope

/**
 * Error normalizado del API. Toda la UI decide sobre esto, nunca sobre la
 * `Response` cruda ni sobre el `HTTPError` de ky.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: Record<string, string[]>
  /**
   * `error.details` tal como vino, sin interpretar.
   *
   * No es decorativo: el 409 de documentos trae ahí `current`, el documento
   * **completo** tal como está en el servidor. Sin esto había que pedirlo con
   * un GET extra —una segunda ida que podía traer una tercera versión— para
   * poder mostrar las dos lado a lado.
   */
  readonly details: Record<string, unknown>
  readonly requestId: string | null
  override readonly cause: unknown

  constructor(params: {
    message: string
    status: number
    code?: string
    fieldErrors?: Record<string, string[]>
    details?: Record<string, unknown>
    requestId?: string | null
    cause?: unknown
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code ?? 'unknown_error'
    this.fieldErrors = params.fieldErrors ?? {}
    this.details = params.details ?? {}
    this.requestId = params.requestId ?? null
    this.cause = params.cause
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  /**
   * Un recurso de otra empresa devuelve 404, no 403 (plan 2.4): un 403
   * confirmaria que el recurso existe.
   */
  get isNotFound(): boolean {
    return this.status === 404
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  get isValidation(): boolean {
    return this.status === 422
  }

  get isNetwork(): boolean {
    return this.status === 0
  }

  get isServer(): boolean {
    return this.status >= 500
  }

  /** Reintentar tiene sentido en red y en 5xx; en 403 o 422 no. */
  get isRetryable(): boolean {
    return this.isNetwork || this.isServer || this.status === 408 || this.status === 429
  }
}

const STATUS_MESSAGE: Record<number, string> = {
  0: 'No pudimos conectar con el servidor. Revisá tu conexión.',
  400: 'La solicitud no es válida.',
  401: 'Tu sesión expiró. Volvé a entrar.',
  403: 'No tenés permiso para hacer esto.',
  404: 'No encontramos lo que buscabas.',
  409: 'Alguien modificó esto antes que vos.',
  422: 'Revisá los datos del formulario.',
  429: 'Demasiadas solicitudes. Probá de nuevo en un momento.',
  500: 'Algo se rompió de nuestro lado.',
  502: 'El servidor no está respondiendo.',
  503: 'El servicio no está disponible en este momento.',
  504: 'El servidor tardó demasiado en responder.',
}

function messageForStatus(status: number): string {
  return STATUS_MESSAGE[status] ?? (status >= 500 ? STATUS_MESSAGE[500]! : 'Ocurrió un error.')
}

function extractDetail(body: ApiErrorEnvelope): string | undefined {
  const enveloped = body.error?.message
  if (typeof enveloped === 'string' && enveloped.trim()) return enveloped
  if (typeof body.detail === 'string' && body.detail.trim()) return body.detail
  if (Array.isArray(body.detail)) {
    const first = body.detail.find((item) => item?.msg)
    if (first?.msg) return first.msg
  }
  if (typeof body.message === 'string' && body.message.trim()) return body.message
  return undefined
}

function extractCode(body: ApiErrorEnvelope, status: number): string {
  return body.error?.code ?? body.code ?? `http_${status}`
}

/**
 * `loc: ["body", "title"]` -> `title`. El primer segmento dice DE DÓNDE viene
 * el campo (body, query, path), que a un formulario no le sirve: lo que
 * `react-hook-form` necesita para marcar el input en rojo es el nombre.
 */
function fieldName(loc: (string | number)[] | undefined): string | null {
  if (!loc?.length) return null
  const parts = loc.filter((part) => part !== 'body' && part !== 'query' && part !== 'path')
  return parts.length ? parts.map(String).join('.') : null
}

function extractFieldErrors(body: ApiErrorEnvelope): Record<string, string[]> {
  if (body.errors && Object.keys(body.errors).length) return body.errors

  const raw = body.error?.details?.fields ?? (Array.isArray(body.detail) ? body.detail : [])
  const out: Record<string, string[]> = {}
  for (const item of raw) {
    const name = fieldName(item?.loc)
    if (!name || !item?.msg) continue
    ;(out[name] ??= []).push(item.msg)
  }
  return out
}

/**
 * Convierte cualquier cosa que tire la capa de red en un `ApiError`.
 * Nunca lanza: si el body no es JSON, se queda con el mensaje del status.
 */
export async function toApiError(error: unknown): Promise<ApiError> {
  if (error instanceof ApiError) return error

  if (error instanceof HTTPError) {
    const { response } = error
    let body: ApiErrorEnvelope = {}
    try {
      body = (await response.clone().json()) as ApiErrorEnvelope
    } catch {
      /* respuesta sin JSON (proxy, html de nginx, 502): nos quedamos con el status */
    }

    return new ApiError({
      message: extractDetail(body) ?? messageForStatus(response.status),
      status: response.status,
      code: extractCode(body, response.status),
      fieldErrors: extractFieldErrors(body),
      details: body.error?.details ?? {},
      // El id viaja SIEMPRE en el header; en el cuerpo casi nunca. Es lo que
      // permite buscar la traza del servidor a partir de un error de pantalla.
      requestId: body.request_id ?? response.headers.get('x-request-id'),
      cause: error,
    })
  }

  if (error instanceof TimeoutError) {
    return new ApiError({
      message: 'El servidor tardó demasiado en responder.',
      status: 0,
      code: 'timeout',
      cause: error,
    })
  }

  return new ApiError({
    message: error instanceof Error ? error.message : STATUS_MESSAGE[0]!,
    status: 0,
    code: 'network_error',
    cause: error,
  })
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}
